import * as React from 'react'
import { useEffect } from 'react'

import { Box, SearchField, SortDirection, Table, Text } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { isEmpty, orderBy } from 'lodash'

import { observer } from 'mobx-react'

import useMultiFilters from 'modules/core/components/MultiFilters/hooks/useMultiFilters'
import createComputed from 'modules/core/hooks/createComputed'
import usePagination from 'modules/core/hooks/usePagination'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'
import { matchText } from 'modules/core/lib/stringUtils'

import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import variables from 'modules/documents/store/collections/variables'
import type Variable from 'modules/documents/store/models/Variable'
import type { ITemplateVariable } from 'modules/documents/store/models/Variable'

import { default as EmptyList } from './EmptyList'
import getFilterValue from './Filters/getFilterValue'
import { default as Header } from './Header'
import { default as TableRow } from './TableRow'

interface Props {
  contractVersions: ContractVersions
}

const useSearchFilter = createComputed((search: string, collection: Variable[]) => {
  return collection.filter((model) => isEmpty(search) || matchText(search, model.attributes.name))
})

const orderVariables = createComputed(
  (filteredVariables: Variable[], attribute: keyof ITemplateVariable, direction: SortDirection) => {
    return orderBy(
      filteredVariables,
      (filteredVariable: Variable) => filteredVariable.attributes[attribute],
      [direction]
    )
  }
)

const AllVariablesTable = ({ contractVersions }: Props) => {
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const [direction, setDirection] = React.useState<SortDirection>('asc')
  const [attribute, setAttribute] = React.useState<keyof ITemplateVariable>('section')

  const { filteredModels, onApplyFilters } = useMultiFilters(
    getFilterValue,
    LOCAL_STORAGE_KEYS.allVariablesFilters
  )

  const filteredVariables = useSearchFilter(debouncedSearch, filteredModels).get()

  const variablesToShow = orderVariables(filteredVariables, attribute, direction).get()

  const onSort = (attribute: string, direction: SortDirection) => {
    setDirection(direction)
    setAttribute(attribute as keyof ITemplateVariable)
  }

  useEffect(() => {
    onApplyFilters(variables.models, contractVersions)
  }, [variables.length, attribute, direction]) // eslint-disable-line react-hooks/exhaustive-deps

  const { paginatedItems, loadMore, hasMore, page } = usePagination({
    batchSize: 50,
    items: variablesToShow,
  })

  return (
    <>
      <Table
        options={() => (
          <Box width="full">
            <SearchField
              variant="big"
              onSearch={onSearch}
              search={search}
              placeholder={i18n.t('common.search')}
            />
          </Box>
        )}
        fullWidth
        bottomSticky
        optionsBottom={() => (
          <Box width="full">
            <Text.Normal size="100" weight="medium">
              {i18n.tp('common.showing.items', { count: variablesToShow.length })}
            </Text.Normal>
          </Box>
        )}
      >
        <Header onSort={onSort} attribute={attribute} direction={direction} />
        {variablesToShow?.length ? (
          <Table.BodyWithInfiniteScroll
            page={page}
            hasMore={hasMore}
            loadMore={loadMore}
            colSpan={5}
          >
            {paginatedItems.map((variable, index) => (
              <TableRow key={index} variable={variable} />
            ))}
          </Table.BodyWithInfiniteScroll>
        ) : (
          <EmptyList />
        )}
      </Table>
    </>
  )
}
export default observer(AllVariablesTable)
